import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Check, 
  X, 
  Upload, 
  Loader2,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to create image from URL
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

// Helper to get cropped image as blob
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // Create thumbnail (150x150)
  const thumbCanvas = document.createElement('canvas');
  const thumbCtx = thumbCanvas.getContext('2d');
  thumbCanvas.width = 150;
  thumbCanvas.height = 150;
  thumbCtx.drawImage(canvas, 0, 0, pixelCrop.width, pixelCrop.height, 0, 0, 150, 150);

  return new Promise((resolve) => {
    canvas.toBlob(
      (originalBlob) => {
        thumbCanvas.toBlob(
          (thumbBlob) => {
            resolve({ originalBlob, thumbBlob });
          },
          'image/jpeg',
          0.85
        );
      },
      'image/jpeg',
      0.9
    );
  });
}

/**
 * AvatarUploader - Component for selecting and cropping avatar images
 * 
 * @param {string} currentAvatarUrl - URL of current avatar image
 * @param {string} currentEmoji - Emoji to show when no avatar
 * @param {function} onAvatarChange - Callback when avatar changes (receives URL)
 * @param {boolean} deferUpload - If true, returns blob instead of uploading (for form submissions)
 * @param {function} onBlobReady - Callback when blob is ready (only used when deferUpload=true)
 */
export default function AvatarUploader({ 
  currentAvatarUrl, 
  currentEmoji = '😊',
  onAvatarChange,
  deferUpload = false,
  onBlobReady
}) {
  const { token, user, refreshUser, api } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // For deferred upload preview

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máx 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    });
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setUploading(true);
    try {
      // Get cropped images
      const { originalBlob, thumbBlob } = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);

      // If deferUpload is true, just return the blob and preview URL
      if (deferUpload) {
        // Create a local URL for preview
        const localPreviewUrl = URL.createObjectURL(originalBlob);
        setPreviewUrl(localPreviewUrl);
        
        // Notify parent with preview URL for display
        if (onAvatarChange) {
          onAvatarChange(localPreviewUrl);
        }
        
        // Also provide the blobs for later upload (both original and thumb)
        if (onBlobReady) {
          onBlobReady({ originalBlob, thumbBlob });
        }
        
        setDialogOpen(false);
        setImageSrc(null);
        toast.success('Imagem selecionada!');
        return;
      }

      // Immediate upload mode (default behavior)
      // Step 1: Get presigned URLs for both original and thumbnail
      const originalUrlRes = await fetch(`${API_URL}/api/users/avatar/upload-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_type: 'image/jpeg',
          file_type: 'original'
        })
      });

      if (!originalUrlRes.ok) throw new Error('Erro ao obter URL de upload');
      const { upload_url: originalUploadUrl, file_key: originalKey } = await originalUrlRes.json();

      const thumbUrlRes = await fetch(`${API_URL}/api/users/avatar/upload-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_type: 'image/jpeg',
          file_type: 'thumbnail'
        })
      });

      if (!thumbUrlRes.ok) throw new Error('Erro ao obter URL de upload do thumbnail');
      const { upload_url: thumbUploadUrl, file_key: thumbKey } = await thumbUrlRes.json();

      // Step 2: Upload both images to R2
      const [originalUpload, thumbUpload] = await Promise.all([
        fetch(originalUploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: originalBlob
        }),
        fetch(thumbUploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: thumbBlob
        })
      ]);

      if (!originalUpload.ok || !thumbUpload.ok) {
        throw new Error('Erro no upload das imagens');
      }

      // Step 3: Confirm upload and update user profile
      const confirmRes = await fetch(`${API_URL}/api/users/avatar/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          original_key: originalKey,
          thumbnail_key: thumbKey,
          original_size: originalBlob.size,
          thumbnail_size: thumbBlob.size
        })
      });

      if (!confirmRes.ok) throw new Error('Erro ao confirmar upload');

      const result = await confirmRes.json();

      toast.success('Avatar atualizado com sucesso!');
      setDialogOpen(false);
      setImageSrc(null);
      
      // Notify parent and refresh user data
      if (onAvatarChange) {
        onAvatarChange(result.avatar_url);
      }
      if (refreshUser) {
        refreshUser();
      }

    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Erro ao salvar avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setImageSrc(null);
  };

  const resetCrop = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  // Use preview URL if available (deferred mode), otherwise use current avatar
  const displayUrl = previewUrl || currentAvatarUrl;

  return (
    <>
      {/* Avatar display with upload trigger */}
      <div className="relative group">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
          {displayUrl ? (
            <img 
              src={displayUrl} 
              alt="Avatar" 
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-3xl">{currentEmoji}</span>
          )}
        </div>
        
        {/* Upload overlay */}
        <label className="absolute inset-0 rounded-full cursor-pointer bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Camera className="w-6 h-6 text-white" />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="avatar-file-input"
          />
        </label>
      </div>

      {/* Crop Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajustar Foto de Perfil</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Crop area */}
            <div className="relative h-64 bg-slate-900 rounded-xl overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              )}
            </div>

            {/* Zoom control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">Zoom</span>
                <Button variant="ghost" size="sm" onClick={resetCrop}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Resetar
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-slate-400" />
                <Slider
                  value={[zoom]}
                  min={1}
                  max={3}
                  step={0.1}
                  onValueChange={([value]) => setZoom(value)}
                  className="flex-1"
                />
                <ZoomIn className="w-4 h-4 text-slate-400" />
              </div>
            </div>

            {/* Preview hint */}
            <p className="text-xs text-center text-slate-500">
              Arraste para posicionar • Use o slider para ajustar o zoom
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={uploading}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={uploading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {deferUpload ? 'Processando...' : 'Salvando...'}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {deferUpload ? 'Usar esta imagem' : 'Salvar'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
